import React, { useState } from "react";
import styled from "styled-components";
import PropTypes from "prop-types";
import { useIntl } from "react-intl";
import { formatCurrency } from "../utils";
import locales from "../locales";
import SeQuraLogo from "./CustomIcons/images-sequra-sequra";
import "./fonts/hafferSQ.css";
import DiscountIcon from "./CustomIcons/images-icons-discount";
import ProfileIcon from "./CustomIcons/images-icons-profile-circle";
import ShieldIcon from "./CustomIcons/images-icons-shield-check";

const Wrap = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 32px;
  padding: 32px 0;
  width: 100%;
  font-family: "HafferSQ", sans-serif;
  font-variant-numeric: proportional-nums;
`;

const SchoolHeader = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 8px;
  width: 100%;
`;

const CircularAvatar = styled.div`
  width: 64px;
  height: 64px;
  border-radius: 50%;
  border: 1px solid #f4f4f4;
  overflow: hidden;
  display: flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;

  img {
    width: 100%;
    height: 100%;
    object-fit: cover;
  }
`;

const CircularAvatarFallback = styled.div`
  width: 100%;
  height: 100%;
  border-radius: 50%;
  background-color: #e8e8e8;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 20px;
  font-weight: 500;
  color: #606060;
  text-transform: uppercase;
`;

const SchoolName = styled.p`
  font-size: 20px;
  color: #1c1c1c;
  letter-spacing: -0.01em;
  margin: 0;
  text-align: center;
  font-weight: 500;
`;

const SummaryCard = styled.div`
  background: #ffffff;
  border: 1px solid #e0e0e0;
  border-radius: 24px;
  padding: 16px;
  width: 100%;
  display: flex;
  flex-direction: column;
  gap: 16px;
`;

const CardTitle = styled.p`
  font-size: 16px;
  font-weight: 600;
  color: #141414;
  margin: 0;
`;

const LineItems = styled.div`
  display: flex;
  flex-direction: column;
  gap: 8px;
`;

const Row = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  width: 100%;
`;

const RowText = styled.span`
  font-size: 16px;
  color: #141414;
`;

const DiscountTag = styled.div`
  display: flex;
  align-items: center;
  gap: 4px;
  background: #f3e8ff;
  border: 1px solid #e9d5ff;
  border-radius: 4px;
  padding: 4px 8px;
  height: 24px;
`;

// Visually center the text with the icon
const DiscountTagLabel = styled.span`
  font-size: 12px;
  position: relative;
  top: 1px;
  font-weight: 500;
  color: #141414;
`;

const DiscountAmount = styled.span`
  font-size: 16px;
  color: #9333ea;
`;

const Divider = styled.div`
  height: 1px;
  background: #f4f4f4;
  width: 100%;
`;

const TotalRow = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  width: 100%;
  font-size: 16px;
  font-weight: 500;
  color: #141414;
`;

const PoweredBySection = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 8px;
  padding: 0 16px;
`;

const PoweredByLabel = styled.span`
  font-size: 12px;
  color: #141414;
`;

const PoweredByLogo = styled.div`
  width: 104px;

  svg {
    display: block;
    width: 100%;
    height: auto;
  }
`;

const InfoPanel = styled.div`
  background: #effbf8;
  border-radius: 24px;
  padding: 16px;
  width: 100%;
  display: flex;
  flex-direction: column;
  gap: 16px;
`;

const InfoPanelTitle = styled.p`
  font-size: 16px;
  font-weight: 600;
  color: #141414;
  margin: 0;
`;

const InfoRow = styled.div`
  display: flex;
  gap: 8px;
  align-items: flex-start;
`;

const IconBadge = styled.div`
  width: 24px;
  height: 24px;
  background: #aeeadb;
  border-radius: 8px;
  display: flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
`;

const InfoText = styled.p`
  font-size: 16px;
  color: #141414;
  margin: 0;
  flex: 1;
  line-height: 1.5;

  strong {
    font-weight: 500;
  }
`;

const EducationCartSummary = ({
  merchantName,
  merchantLogo,
  courseItems,
  discount,
  handling,
  registration,
  setupFee,
  totalAmount,
  currency,
  locale,
}) => {
  const intl = useIntl();
  const [logoError, setLogoError] = useState(false);

  return (
    <Wrap>
      <SchoolHeader>
        <CircularAvatar>
          {merchantLogo && !logoError ? (
            <img
              src={merchantLogo}
              alt={`${merchantName} logo`}
              onError={() => setLogoError(true)}
            />
          ) : (
            merchantName && (
              <CircularAvatarFallback>
                {merchantName.charAt(0)}
              </CircularAvatarFallback>
            )
          )}
        </CircularAvatar>
        <SchoolName>{merchantName}</SchoolName>
      </SchoolHeader>

      <SummaryCard>
        <CardTitle>{intl.formatMessage(locales.cart_summary)}</CardTitle>
        <LineItems>
          {courseItems.map((item) => (
            <Row key={item.id}>
              <RowText>{item.name}</RowText>
              <RowText>
                {formatCurrency(item.price_with_tax / 100, currency, locale)}
              </RowText>
            </Row>
          ))}

          {discount && (
            <Row>
              <DiscountTag>
                <DiscountIcon />
                <DiscountTagLabel>
                  {intl.formatMessage(locales.discount)}
                </DiscountTagLabel>
              </DiscountTag>
              <DiscountAmount>-{discount}</DiscountAmount>
            </Row>
          )}

          {handling.total_with_tax && (
            <Row>
              <RowText>{intl.formatMessage(locales.handling)}</RowText>
              <RowText>{handling.total_with_tax}</RowText>
            </Row>
          )}

          {registration.total_with_tax && (
            <Row>
              <RowText>{intl.formatMessage(locales.registration)}</RowText>
              <RowText>{registration.total_with_tax}</RowText>
            </Row>
          )}

          {setupFee.total_with_tax?.value && (
            <Row>
              <RowText>{intl.formatMessage(locales.setup_fee)}</RowText>
              <RowText>{setupFee.total_with_tax.value}</RowText>
            </Row>
          )}

          <Divider />

          <TotalRow>
            <span>{intl.formatMessage(locales.total)}</span>
            <span>{totalAmount}</span>
          </TotalRow>
        </LineItems>
      </SummaryCard>

      <PoweredBySection>
        <PoweredByLabel>
          {intl.formatMessage(locales.education_powered_by)}
        </PoweredByLabel>
        <PoweredByLogo>
          <SeQuraLogo />
        </PoweredByLogo>
      </PoweredBySection>

      <InfoPanel>
        <InfoPanelTitle>
          {intl.formatMessage(locales.education_checkout_info_title)}
        </InfoPanelTitle>
        <InfoRow>
          <IconBadge>
            <ProfileIcon />
          </IconBadge>
          <InfoText>
            {intl.formatMessage(locales.education_checkout_info_payer, {
              b: (chunks) => <strong>{chunks}</strong>,
            })}
          </InfoText>
        </InfoRow>
        <InfoRow>
          <IconBadge>
            <ShieldIcon />
          </IconBadge>
          <InfoText>
            {intl.formatMessage(locales.education_checkout_info_camera, {
              b: (chunks) => <strong>{chunks}</strong>,
            })}
          </InfoText>
        </InfoRow>
      </InfoPanel>
    </Wrap>
  );
};

EducationCartSummary.propTypes = {
  merchantName: PropTypes.string,
  merchantLogo: PropTypes.string,
  courseItems: PropTypes.arrayOf(
    PropTypes.shape({
      id: PropTypes.string,
      name: PropTypes.string,
      price_with_tax: PropTypes.number,
    })
  ),
  discount: PropTypes.string,
  handling: PropTypes.shape({ total_with_tax: PropTypes.string }),
  registration: PropTypes.shape({ total_with_tax: PropTypes.string }),
  setupFee: PropTypes.shape({
    total_with_tax: PropTypes.shape({ value: PropTypes.string }),
  }),
  totalAmount: PropTypes.string,
  currency: PropTypes.string,
  locale: PropTypes.string,
};

EducationCartSummary.defaultProps = {
  merchantName: "",
  merchantLogo: "",
  courseItems: [],
  discount: null,
  handling: {},
  registration: {},
  setupFee: {},
  totalAmount: null,
  currency: "EUR",
  locale: "es-ES",
};

export default EducationCartSummary;
